import { Link } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { integrations } from '@/content/integrations';
import { useSeo } from '@/seo/useSeo';

export function Integrations(): JSX.Element {
  useSeo({
    title: 'Integrations',
    description:
      'Syncro migration sync, FortiGate/Omada/Meraki/UniFi network monitoring, Jamf, Intune, NinjaOne, Action1, Google Workspace, BCDR vendor monitoring, and Dell/Lenovo warranty lookup — all folded into the same Nexus console instead of a separate portal per vendor.',
    path: '/integrations',
    schema: {
      '@type': 'ItemList',
      itemListElement: integrations.map((integ, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://nexusrmm.ai/integrations/${integ.slug}`,
        name: integ.name,
      })),
    },
  });
  return (
    <div>
      <PageHero
        eyebrow="Integrations"
        title="One console, whatever you're already running."
        subtitle="Nexus doesn't ask you to replace every tool on day one. Existing PSA data, MDM fleets, network gear, and patch tooling fold into the same console instead of a separate portal per vendor."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {integrations.map((integ, i) => {
              const Icon = integ.icon;
              return (
                <Reveal key={integ.slug} delay={(i % 3) * 80}>
                  <Link
                    to={`/integrations/${integ.slug}`}
                    className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                  >
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                      <Icon fontSize="small" />
                    </div>
                    <h2 className="font-display text-[17px] font-semibold text-nexus-ink">{integ.name}</h2>
                    <p className="mt-1.5 flex-1 text-sm text-white/65">{integ.summary}</p>
                    <Chip
                      label={integ.category}
                      size="small"
                      className="mt-4 h-[22px] w-fit border border-white/15 bg-transparent text-[10.5px] font-semibold text-white/55"
                    />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Don&apos;t see what you&apos;re running today?
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Tell us your current stack — new integrations follow the same read-oriented,
                simulation-until-configured pattern as everything above.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
